import React, {memo} from "react";
import PropTypes from "prop-types";
import {Box, Card, CardContent, Chip, Typography} from "@mui/material";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import ListAltIcon from "@mui/icons-material/ListAlt";
import FitnessCenterOutlinedIcon from "@mui/icons-material/FitnessCenterOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import dayjs from "dayjs";
import {useNotificationCardStyles} from "./notificationCenterStyles";

const MODEL_ICON_MAP = {
  incident: GppMaybeOutlinedIcon,
  action: ListAltIcon,
  activity: FitnessCenterOutlinedIcon,
  obligation: PublicOutlinedIcon,
  playbook: LibraryBooksOutlinedIcon,
  report: DescriptionOutlinedIcon,
  task: AssignmentOutlinedIcon,
  meeting: VideocamOutlinedIcon,
};

const NotificationCard = memo(({notification, onClick}) => {
  const {classes, cx} = useNotificationCardStyles();
  const IconComponent = MODEL_ICON_MAP[notification.model] || NotificationsNoneOutlinedIcon;

  return (
    <Card
      className={cx(classes.card, notification.isRead && classes.cardRead)}
      onClick={() => onClick(notification)}
      data-testid={`notification-card-${notification.id}`}
    >
      <CardContent className={classes.cardContent}>
        <Box sx={{display: "flex", gap: 1}}>
          <IconComponent className={classes.icon} data-testid="notification-icon" />
          <Box sx={{flex: 1, minWidth: 0}}>
            <Box className={classes.header}>
              {notification.badgeText && (
                <Chip
                  label={notification.badgeText}
                  size="small"
                  sx={{
                    backgroundColor: notification.badgeColor,
                    color: notification.badgeForegroundColor || "#fff",
                  }}
                />
              )}
              <Box sx={{flex: 1}} />
              {!notification.isSeen && (
                <Chip label="New" size="small" className={classes.newChip} />
              )}
            </Box>
            <Typography variant="subtitle2" noWrap>
              {notification.header}
            </Typography>
            {notification.subtext && (
              <Typography variant="body2" color="textSecondary" noWrap>
                {notification.subtext}
              </Typography>
            )}
            <Typography variant="caption" className={classes.meta}>
              {notification.initiatorName && `${notification.initiatorName} · `}
              {dayjs(notification.createdAt).format("MMM D, YYYY h:mm A")}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
});

NotificationCard.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    header: PropTypes.string,
    subtext: PropTypes.string,
    initiatorName: PropTypes.string,
    isRead: PropTypes.bool,
    isSeen: PropTypes.bool,
    createdAt: PropTypes.string,
    model: PropTypes.string,
    badgeText: PropTypes.string,
    badgeColor: PropTypes.string,
    badgeForegroundColor: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

export {MODEL_ICON_MAP};
export default NotificationCard;
